import { isNotDefined } from '@sgme/fp';
import { isEqual as deepEqual, difference } from 'lodash-es';
import type { Action } from 'state/actions';
import { isDevEnvironment } from 'utils/environment';
import { getFrontLogger } from 'utils/loggers/FrontLogger';
import { getLastActions } from 'utils/redux/catchMiddleware';

import type { Collection, Patcher, Predicate } from './types';

export interface TrackingContext {
  action: Action;
  reducer?: string;
}

export function addWorkspaceKey<
  W extends string,
  T extends { workspace: W },
  C extends Collection<T, K>,
  K extends string = string,
>(state: C, key: K, value: T, trackingContext?: TrackingContext) {
  return addKey<T, C, K>(state, key, value, trackingContext);
}
export function addKey<T, C extends Collection<T, K>, K extends string = string>(
  state: C,
  key: K,
  value: T,
  trackingContext?: TrackingContext,
): C {
  if (state[key] !== undefined) {
    if (isDevEnvironment()) {
      throw new Error(
        trackingContext
          ? `addKey cannot overwrite existing key '${key}' while reducing ${JSON.stringify({
              [key]: state[key],
            })} with '${JSON.stringify(trackingContext)}'`
          : `addKey cannot overwrite existing key '${key}' while reducing ${JSON.stringify({ [key]: state[key] })}`,
      ); // fail fast in dev
    } else {
      if (trackingContext) {
        getFrontLogger().error(
          `Reducer: addKey cannot overwrite existing key {@Key} while reducing {@State} with {@TrackingContext}`,
          key,
          JSON.stringify({
            [key]: state[key],
          }),
          JSON.stringify({ ...trackingContext, lastActions: getLastActions() }),
        );
      } else {
        getFrontLogger().error(
          `Reducer: addKey cannot overwrite existing key {@Key} while reducing {@State} with {@TrackingContext}`,
          key,
          JSON.stringify({
            [key]: state[key],
          }),
          JSON.stringify({ lastActions: getLastActions() }),
        );
      }
    }
  }
  return { ...state, [key]: value };
}

export function addKeys<T, C extends Collection<T, K>, K extends string = string>(
  state: C,
  keys: readonly K[],
  values: readonly T[],
  trackingContext?: TrackingContext,
): C {
  return keys.reduce((result, key, index) => addKey(result, key, values[index], trackingContext), state);
}

export function addOrReplaceKey<T, K extends string = string>(
  state: Collection<T, K>,
  key: K,
  value: T,
): Collection<T, K> {
  return { ...state, [key]: value };
}

export function setKey<T, K extends string = string>(key: K, value: T): Collection<T, K> {
  return { [key]: value } as Collection<T, K>;
}

export function replaceKeyIfExist<T, K extends string = string>(
  state: Collection<T, K>,
  key: K,
  replace: (old: T) => T,
): Collection<T, K> {
  const oldValue = state[key];
  if (isNotDefined(oldValue)) {
    return state;
  }
  const newValue = replace(oldValue);
  return { ...state, [key]: newValue };
}

export function addOrReplaceKeys<T, K extends string = string>(
  state: Collection<T, K>,
  keyValues: [K, T][],
): Collection<T, K> {
  if (keyValues.length === 0) {
    return state;
  }
  const updatedState: Record<K, T> = { ...state };
  keyValues.forEach(kv => {
    updatedState[kv[0]] = kv[1];
  });
  return updatedState;
}

export function addOrPatchKeys<T, K extends string = string>(
  state: Collection<T, K>,
  keyValues: [K, T][],
): Collection<T, K> {
  if (keyValues.length === 0) {
    return state;
  }
  const updatedState: Record<K, T> = { ...state };
  keyValues.forEach(kv => {
    updatedState[kv[0]] = { ...updatedState[kv[0]], ...kv[1] };
  });
  return updatedState;
}

/**
 *
 * @param state
 * @param key
 * @param patcher
 * @param trackingContext for error tracking purpose (if key not already exists)
 * @returns
 */
export function patchKey<T, C extends Collection<T, K>, K extends string = string>(
  state: C,
  key: K,
  patcher: Patcher<C[K]>,
  trackingContext?: TrackingContext,
): C {
  const originKeyState = state[key];

  if (originKeyState === undefined) {
    if (isDevEnvironment()) {
      throw new Error(
        trackingContext
          ? `patchKey cannot overwrite non existing key ${key} while reducing with ${JSON.stringify(trackingContext)}`
          : `patchKey cannot overwrite non existing key ${key} while reducing ${JSON.stringify(state)}`,
      ); // fail fast in dev
    } else {
      if (trackingContext) {
        getFrontLogger().error(
          `Reducer: patchKey cannot overwrite non existing key {@Key} while reducing with {@TrackingContext}`,
          key,
          JSON.stringify({ ...trackingContext, lastActions: getLastActions() }),
        );
      } else {
        const firstStateKey = Object.keys(state)[0] as K | undefined;
        getFrontLogger().error(
          `Reducer: patchKey cannot overwrite non existing key {@Key} while reducing {@State} with {@TrackingContext}`,
          key,
          JSON.stringify(firstStateKey ? { [firstStateKey]: state[firstStateKey] } : {}),
          JSON.stringify({ lastActions: getLastActions() }),
        );
      }
    }
    return state;
  }

  const keyPatch = patcher(originKeyState);
  return {
    ...state,
    [key]: {
      ...state[key],
      ...keyPatch,
    } as T,
  };
}

export function patchKeyIfExist<T, C extends Collection<T, K>, K extends string = string>(
  state: C,
  key: K,
  patcher: Patcher<C[K]>,
): C {
  const originKeyState = state[key];

  if (originKeyState === undefined) {
    return state;
  }

  const keyPatch = patcher(originKeyState);
  return {
    ...state,
    [key]: {
      ...state[key],
      ...keyPatch,
    } as T,
  };
}

export function addCompositeKey<
  T,
  C extends Collection<Collection<T, K2>, K1>,
  K1 extends string = string,
  K2 extends string = string,
>(state: C, key1: K1, key2: K2, value: T, trackingContext?: TrackingContext): C {
  return addOrPatchKey(state, key1, intermediateState => addKey(intermediateState, key2, value, trackingContext));
}

export function addOrPatchCompositeKey<
  T,
  C extends Collection<Collection<T, K2>, K1>,
  K1 extends string = string,
  K2 extends string = string,
>(state: C, key1: K1, key2: K2, patcher: Patcher<C[K1][K2]>): C {
  return addOrPatchKey(state, key1, intermediateState => addOrPatchKey(intermediateState, key2, patcher));
}

export function patchCompositeKey<
  T,
  C extends Collection<Collection<T, K2>, K1>,
  K1 extends string = string,
  K2 extends string = string,
>(state: C, key1: K1, key2: K2, patcher: Patcher<C[K1][K2]>, trackingContext?: TrackingContext): C {
  return patchKey(state, key1, intermediateState => patchKey(intermediateState, key2, patcher, trackingContext));
}

export function removeCompositeKey<
  T,
  C extends Collection<Collection<T, K2>, K1>,
  K1 extends string = string,
  K2 extends string = string,
>(state: C, key1: K1, key2: K2, trackingContext?: TrackingContext): C {
  const intermediateState = state[key1];
  if (!intermediateState) {
    return state;
  }

  const keys = Object.keys(intermediateState);

  if (keys.length === 1 && keys.indexOf(key2) > -1) {
    return removeKey(state, key1);
  }

  return patchKey(state, key1, s => removeKey(s, key2), trackingContext);
}

export function addOrPatchKey<T, C extends Collection<T, K>, K extends string = string>(
  state: C,
  key: K,
  patcher: Patcher<C[K]>,
  initialState?: C[K],
): C {
  const previousKeyState = (state[key] || (initialState ?? {})) as C[K];
  const keyPatch = patcher(previousKeyState);
  if (isNotDefined(key)) {
    return state;
  }
  return {
    ...state,
    [key]: {
      ...previousKeyState,
      ...keyPatch,
    } as T,
  };
}

export function patchKeysByPredicate<T, C extends Collection<T, K>, K extends string = string>(
  state: C,
  predicate: Predicate<C[K]>,
  patcher: Patcher<C[K]>,
): C {
  const matchingKeys = (Object.keys(state) as K[]).filter(key => predicate(state[key]));
  return patchKeys(state, matchingKeys as K[], patcher);
}

export function patchKeys<T, C extends Collection<T, K>, K extends string = string>(
  state: C,
  keys: readonly K[],
  patcher: Patcher<C[K]>,
  trackingContext?: TrackingContext,
): C {
  if (keys.length === 0) {
    return state;
  }
  return keys.reduce((result, key) => patchKey(result, key, patcher, trackingContext), state);
}

export function patchKeysIfExists<T, C extends Collection<T, K>, K extends string = string>(
  state: C,
  keys: K[],
  patcher: Patcher<C[K]>,
): C {
  if (keys.length === 0) {
    return state;
  }
  return keys.reduce((result, key) => patchKeyIfExist(result, key, patcher), state);
}

export function removeKey<T, C extends Collection<T, K>, K extends string = string>(state: C, key?: K | null): C {
  if (key === undefined || key === null || state[key] === undefined) {
    return state;
  }
  const { [key]: _, ...rest } = state;
  return rest as C;
}

export function removeKeyIfEmpty<T, C extends Collection<T, K>, K extends string = string>(state: C, key: K): C {
  if (state[key] === undefined) {
    return state;
  }
  const value = state[key];
  if (typeof value === 'object' && Object.keys(value as object).length === 0) {
    const { [key]: _, ...rest } = state;
    return rest as C;
  }
  return state;
}
export function removeKeys<T, C extends Collection<T, K>, K extends string = string>(state: C, keys: readonly K[]): C {
  if (keys.length === 0) {
    return state;
  }
  return keys.reduce((result, key) => removeKey(result, key), state);
}
export function keepKeys<T, C extends Collection<T, K>, K extends string = string>(state: C, keys: readonly K[]): C {
  if (keys.length === 0) {
    return state;
  }
  return difference(Object.keys(state), keys).reduce((result, key) => removeKey(result, key), state);
}

export function renameKey<T, C extends Collection<T, K>, K extends string = string>(
  state: C,
  oldKey?: K,
  newKey?: K,
  trackingContext?: TrackingContext,
): C {
  if (isNotDefined(oldKey) || isNotDefined(newKey) || state[oldKey] === undefined) {
    return state;
  }
  return removeKey(addKey<T, C, K>(state, newKey, state[oldKey], trackingContext), oldKey);
}

export function duplicateKey<T, C extends Collection<T, K>, K extends string = string>(
  state: C,
  keyToDuplicate: K,
  newKey: K,
  trackingContext?: TrackingContext,
): C {
  if (state[keyToDuplicate] === undefined) {
    return state;
  } else if (state[newKey] !== undefined) {
    throw Error(
      `duplicateKey cannot overwrite non existing key '${newKey}' while reducing with '${JSON.stringify(
        trackingContext,
      )}'`,
    );
  }
  return {
    ...state,
    [newKey]: {
      ...state[keyToDuplicate],
    },
  };
}

export function removeIdenticalKeyValues<T, K extends string = string>(
  originalDictionary: Collection<T, K>,
  toRemove: Collection<T, K>,
): Collection<T, K> {
  const keys = (Object.keys(originalDictionary) as K[]).filter(k => deepEqual(originalDictionary[k], toRemove[k]));
  return removeKeys(originalDictionary, keys);
}

export function prependToArray<T>(
  data: readonly T[] | undefined = [],
  items: readonly T[] | undefined = [],
): readonly T[] {
  return items.concat(data);
}

export function patchArray<T>(state: ReadonlyArray<T> | undefined, rowIndex: number, patch: (old: T) => Partial<T>) {
  if (state === undefined || state[rowIndex] === undefined) {
    return state;
  }
  const previous = state[rowIndex];
  const patched = patch(previous);
  return [...state.slice(0, rowIndex), { ...previous, ...patched }, ...state.slice(rowIndex + 1)];
}

export function appendArray<T>(state: ReadonlyArray<T> | undefined, newItem: T) {
  if (state === undefined) {
    return [newItem];
  }
  return [...state, newItem];
}

export function removeArray<T>(state: ReadonlyArray<T> | undefined, rowIndex: number) {
  if (state === undefined) {
    return [];
  }
  return [...state.slice(0, rowIndex), ...state.slice(rowIndex + 1)];
}
